import type { LucideIcon } from "lucide-react";

type Option<T extends string | number> = T | { value: T; label: string };

interface MultiSelectFieldProps<T extends string | number> {
  label: string;
  icon: LucideIcon;
  options: Option<T>[];
  selected: T[];
  onChange: (selected: T[]) => void;
  error?: string;
}

function MultiSelectField<T extends string | number>({
  label,
  icon: Icon,
  options,
  selected,
  onChange,
  error = "",
}: MultiSelectFieldProps<T>) {
  const normalizedOptions = options.map((option) =>
    typeof option === "object" && option !== null && "value" in option && "label" in option
      ? option
      : { value: option, label: String(option) }
  );

  const toggleOption = (optionValue: T) => {
    if (selected.includes(optionValue)) {
      onChange(selected.filter((s) => s !== optionValue));
    } else {
      onChange([...selected, optionValue]);
    }
  };

  return (
    <div>
      <label className="flex items-center text-sm font-medium text-gray-700 mb-3">
        <Icon size={16} className="me-2 text-[#b3d130]" />
        {label}
      </label>
      <div className={`flex flex-wrap gap-2${error ? " p-2 border border-red-300 rounded-xl bg-red-50/30" : ""}`}>
        {normalizedOptions.map((option) => (
          <button
            key={option.value}
            type="button"
            onClick={() => toggleOption(option.value)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 ${
              selected.includes(option.value)
                ? "bg-[#b3d130] text-gray-900 shadow-md"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}

export default MultiSelectField;
